/**
 * Business Associate Agreement Service
 * Handles BAA acceptance tracking and compliance
 * Uses localStorage for UI state with HIPAA audit logging for compliance
 */

import { webStorageService } from './webStorageService';
import { hipaaService } from './hipaaComplianceService';
import { updateUserAttributes, fetchUserAttributes } from 'aws-amplify/auth';

class BAAService {
  /**
   * Check if user has accepted BAA
   * @param {Object} user - Current user object
   * @returns {boolean} True if BAA has been accepted
   */
  async hasBAAAccepted(user) {
    try {
      if (!user) return false;

      // Check Cognito user attributes for BAA acceptance
      const userAttributes = await fetchUserAttributes();

      const baaAccepted = userAttributes['custom:baa_accepted'];
      const baaVersion = userAttributes['custom:baa_version'];

      // Verify BAA is accepted and version is current
      return baaAccepted === 'true' && baaVersion === '2025-09-17';
    } catch (error) {
      console.error('Error checking BAA acceptance from Cognito:', error);
      return false;
    }
  }

  /**
   * Accept BAA for current user
   * @param {Object} user - Current user object
   * @returns {Promise} Promise resolving when BAA is accepted
   */
  async acceptBAA(user) {
    try {
      if (!user) {
        throw new Error('User not provided');
      }

      const acceptanceTimestamp = new Date().toISOString();
      const baaVersion = '2025-09-17';

      // Store BAA acceptance in Cognito user attributes
      await updateUserAttributes({
        userAttributes: {
          'custom:baa_accepted': 'true',
          'custom:baa_version': baaVersion,
          'custom:baa_date': acceptanceTimestamp
        }
      });

      // CRITICAL: Log for HIPAA compliance - this creates the audit trail
      // Make logging non-blocking so BAA acceptance works even if logging fails
      try {
        hipaaService.logAuditEvent({
          action: 'BAA_ACCEPTED',
          details: `Business Associate Agreement accepted - Version: ${baaVersion}, User: ${user.email}, Date: ${acceptanceTimestamp}`,
          level: 'SECURITY',
          userId: user.id,
          userEmail: user.email,
          baaVersion: baaVersion,
          storageMethod: 'cognito_user_attributes'
        });
      } catch (auditError) {
        console.warn('Audit logging failed, but BAA acceptance will continue:', auditError);
      }

      console.log('BAA accepted successfully in Cognito for user:', user.id);
      return true;
    } catch (error) {
      console.error('Error accepting BAA in Cognito:', error);
      throw error;
    }
  }

  /**
   * Get BAA acceptance details for user
   * @param {Object} user - Current user object
   * @returns {Object} BAA acceptance details
   */
  async getBAADetails(user) {
    try {
      if (!user) return null;

      const userAttributes = await fetchUserAttributes();

      const baaAccepted = userAttributes['custom:baa_accepted'];
      const baaVersion = userAttributes['custom:baa_version'];
      const baaDate = userAttributes['custom:baa_date'];

      return {
        accepted: baaAccepted === 'true',
        acceptedDate: baaDate || null,
        version: baaVersion || null,
        userEmail: user.email
      };
    } catch (error) {
      console.error('Error getting BAA details from Cognito:', error);
      return {
        accepted: false,
        acceptedDate: null,
        version: null,
        userEmail: user.email
      };
    }
  }

  /**
   * Get client IP address for audit logging
   * @returns {Promise<string>} Client IP address
   */
  async getClientIP() {
    // For now, return localhost since external IP calls are blocked by CSP
    // In production, IP will be captured by the audit API from request headers
    return 'localhost';
  }

  /**
   * Check if BAA needs to be re-accepted (version updates)
   * @param {Object} user - Current user object
   * @returns {boolean} True if BAA needs re-acceptance
   */
  async needsBAAReacceptance(user) {
    try {
      const details = await this.getBAADetails(user);
      if (!details || !details.accepted) return true;

      // Current BAA version
      const currentVersion = '2025-09-17';

      // If user accepted an older version, they need to re-accept
      return details.version !== currentVersion;
    } catch (error) {
      console.error('Error checking BAA re-acceptance:', error);
      return true; // Err on the side of caution
    }
  }

  /**
   * Clear BAA acceptance (for testing or user logout)
   * @param {Object} user - Current user object
   */
  async clearBAAAcceptance(user) {
    try {
      if (!user) return;

      // Clear BAA acceptance in Cognito user attributes
      await updateUserAttributes({
        userAttributes: {
          'custom:baa_accepted': 'false',
          'custom:baa_version': '',
          'custom:baa_date': ''
        }
      });

      // Log BAA clearing for audit trail
      hipaaService.logAuditEvent({
        action: 'BAA_CLEARED',
        details: `Business Associate Agreement acceptance cleared - User: ${user.email}, Reason: user_logout_or_manual_clear`,
        level: 'INFO',
        userId: user.id,
        userEmail: user.email
      });

      console.log('BAA acceptance cleared in Cognito for user:', user.id);
    } catch (error) {
      console.error('Error clearing BAA acceptance in Cognito:', error);
    }
  }
}

// Create singleton instance
export const baaService = new BAAService();
export default baaService;